import React, { useState } from 'react';
import { Link as LinkIcon, Image, FileText, ExternalLink } from 'lucide-react';
import AttachmentViewer from './AttachmentViewer';

interface PostAttachmentProps {
  type: 'link' | 'image' | 'file';
  url: string;
  title?: string;
  description?: string;
  thumbnailUrl?: string;
}

function PostAttachment({ type, url, title, description, thumbnailUrl }: PostAttachmentProps) {
  const [showViewer, setShowViewer] = useState(false);

  const renderContent = () => {
    switch (type) {
      case 'image':
        return (
          <div className="mt-3 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowViewer(true)}
              className="w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-lg overflow-hidden"
            >
              <img
                src={url}
                alt={title || 'Attached image'}
                className="w-full h-auto max-h-96 object-cover hover:opacity-90 transition-opacity"
              />
            </button>
            <AttachmentViewer
              isOpen={showViewer}
              onClose={() => setShowViewer(false)}
              type="image"
              url={url}
              title={title}
            />
          </div>
        );
      case 'link':
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-start space-x-3">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title || url}
                  className="w-16 h-16 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <LinkIcon className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-indigo-600 flex items-center gap-2">
                  {title || url}
                  <ExternalLink className="h-4 w-4" />
                </p>
                {description && (
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {description}
                  </p>
                )}
              </div>
            </div>
          </a>
        );
      case 'file':
        return (
          <div className="mt-3">
            <button
              onClick={() => setShowViewer(true)}
              className="w-full p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-indigo-600 flex items-center gap-2">
                    {title || 'View file'}
                  </p>
                </div>
              </div>
            </button>
            <AttachmentViewer
              isOpen={showViewer}
              onClose={() => setShowViewer(false)}
              type="file"
              url={url}
              title={title}
            />
          </div>
        );
    }
  };

  return renderContent();
}

export default PostAttachment;